import { Paths } from "common/paths/paths";
import { PERMISSIONS } from "config";
import * as H from "history";
import { UserPermissions } from "lib/services";
import React, { FC, useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import AiForMePage from "view/Page/AiForMe/AiForMe";
import AiFormeLandingPage from "view/Page/AiForMe/AiFormeLandingPage";

// Legacy query params that were used in the old AiForMe page
const LEGACY_QUERY_PARAMS = [
  "sort",
  "jobFamily",
  "dataClassification",
  "query",
  "categories",
  "isUseCaseSearch",
  "showRecommended",
];

/**
 * Redirects users from /aiforme with legacy query params to /aiforme/allAiTools
 * This preserves the user's search/filter intent from bookmarks or old links
 * @returns true if redirect happened, false otherwise
 */
const redirectLegacyQueryParamsToAllAiTools = (location: H.Location, history: H.History): boolean => {
  try {
    const params = new URLSearchParams(location.search);
    const hasLegacyParams = LEGACY_QUERY_PARAMS.some((param) => {
      const value = params.get(param);
      return value !== null && value.trim() !== "";
    });

    if (hasLegacyParams) {
      history.replace(`${Paths.aiForMeAllAiTools.url}${location.search}`);
      return true;
    }
  } catch (error) {
    console.error("Error processing query parameters:", error);
  }
  return false;
};

export const AiForMeRouteComponent: FC = () => {
  const location = useLocation();
  const history = useHistory();
  const [isRedirecting, setIsRedirecting] = useState(false);

  // Determine if we should show the new landing page
  let showNewLandingPage = false;
  try {
    showNewLandingPage = UserPermissions.current.hasPermission(PERMISSIONS.TOGGLE_AI_FOR_ME_LANDING_PAGE);
  } catch {
    showNewLandingPage = false;
  }

  useEffect(() => {
    // Only redirect legacy params when showing the new landing page
    // The old AiForMePage handles these params itself
    if (showNewLandingPage) {
      const didRedirect = redirectLegacyQueryParamsToAllAiTools(location, history);
      setIsRedirecting(didRedirect);
    }
  }, [history, location, location.search, showNewLandingPage]);

  // Don't render anything while redirecting to avoid flash
  if (isRedirecting) {
    return null;
  }

  return showNewLandingPage ? <AiFormeLandingPage /> : <AiForMePage />;
};

/**
 * This component acts as a route guard for the hardware section.
 * Once the shop hardware site is implemented and the corresponding KVS site toggle is enabled,
 * navigating to /hardware within the SPA will trigger a full page reload
 * to retrieve and initialize the new hardware SPA.
 */
export const HardwareRouteReloaderGuard: FC = () => {
  const location = useLocation();
  const RELOAD_FLAG_KEY = "hardware-route-reload-attempted";

  useEffect(() => {
    const fullUrl = `${location.pathname}${location.search}${location.hash}`;

    const belongsToHardware = location.pathname === "/hardware" || location.pathname.startsWith("/hardware/");

    if (!belongsToHardware) {
      // clear on navigation away
      sessionStorage.removeItem(RELOAD_FLAG_KEY);
      return;
    }

    if (sessionStorage.getItem(RELOAD_FLAG_KEY)) {
      // origin routing did not take effect; abort to avoid infinite loop
      return;
    }

    sessionStorage.setItem(RELOAD_FLAG_KEY, "true");
    window.location.replace(fullUrl);
  }, [location.pathname, location.search, location.hash]);

  return null;
};
